import { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import type ManagedAccount from "@tootsweet/model/entities/ManagedAccount";
import {
  setAccountToDisplay,
  useAccountToDisplaySelector,
  useManagedAccountsSelector
} from "../../redux/actions/user";
import {
  JETPACK_CONFIG,
  LEGAL,
  SIGNUP,
  SUBSCRIPTION,
  subscriptionByQuote
} from "../routes/routes";
import {
  deleteMasterTokenFromStorage,
  getLastManagedAccountTrackingID
} from "../helpers/localStorage";
import {
  extractTrackingIDFromURL,
  splitPathName
} from "../helpers/navigation/extractTrackingIDFromURL";

function filterAccounts(account: ManagedAccount, trackingID: string) {
  if (window.location.pathname.includes(SUBSCRIPTION)) {
    return account.trackingID === trackingID;
  }
  return !account?.businessUser?.hide && account.trackingID === trackingID;
}

/**
 * Hook that extracts trackingID from route params and validates it against managed accounts.
 * If trackingID is valid, sets it as accountToDisplay.
 * If trackingID is invalid, defaults to first managed account.
 * If no managed accounts exist, redirects to signup.
 */
export const useTrackingIdRouting = (logTag?: string) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const managedAccounts = useManagedAccountsSelector();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const trackingID = useMemo(() => {
    return extractTrackingIDFromURL();
  }, [window.location.pathname]);

  useEffect(() => {
    if (window.location.pathname.includes(LEGAL)) {
      return;
    }

    if (!managedAccounts || managedAccounts.length === 0) {
      deleteMasterTokenFromStorage();
      history.push(SIGNUP);
      return;
    }

    let matchingAccount = !!trackingID
      ? managedAccounts.find((ma) => filterAccounts(ma, trackingID))
      : null;

    if (matchingAccount) {
      dispatch(setAccountToDisplay(matchingAccount));

      // Redirect /:trackingID to /:trackingID/config
      const split = splitPathName();
      if (split.length < 2) {
        history.push(
          buildPathWithTrackingId(JETPACK_CONFIG, matchingAccount.trackingID)
        );
      }
      return;
    }

    // If no matching account from URL, check localStorage for last used account
    if (!matchingAccount) {
      const lastTrackingID = getLastManagedAccountTrackingID();

      if (lastTrackingID) {
        const lastAccount = managedAccounts.find((ma) =>
          filterAccounts(ma, lastTrackingID)
        );

        if (lastAccount) {
          matchingAccount = lastAccount;
        }
      } else {
      }
    }

    const accountToSet = matchingAccount || managedAccounts[0];
    dispatch(setAccountToDisplay(accountToSet));

    if (window.location.pathname.includes(subscriptionByQuote)) {
      history.push(
        buildPathWithTrackingId(
          subscriptionByQuote,
          accountToSet.trackingID,
          true
        )
      );
    } else {
      history.push(
        buildPathWithTrackingId(JETPACK_CONFIG, accountToSet.trackingID)
      );
    }
  }, [
    trackingID,
    managedAccounts,
    accountToDisplay,
    dispatch,
    history.location.pathname,
    logTag
  ]);

  return {
    trackingID,
    accountToDisplay
  };
};

/**
 * Helper function to build a path with trackingID parameter
 * @param basePath - The base path to navigate to
 * @param trackingID - The tracking ID to prepend to the path
 * @param preserveSearch - Whether to preserve current URL search parameters (default: false)
 * @param search - additional search params
 */
export const buildPathWithTrackingId = (
  basePath: string,
  trackingID: string | undefined,
  preserveSearch = false,
  search?: Record<string, string>
): string => {
  let paramz: URLSearchParams;

  if (preserveSearch) {
    paramz = new URLSearchParams(window.location.search);
  } else {
    paramz = new URLSearchParams();
  }

  if (search) {
    Object.keys(search).forEach((key) => {
      paramz.set(key, search[key]);
    });
  }

  let query = paramz?.toString();

  if (query && !query.startsWith("?")) {
    query = "?" + query;
  }

  if (!trackingID) {
    return basePath + (query ? `${query}` : "");
  }

  return `/${trackingID}${basePath}` + (query ? `${query}` : "");
};

/**
 * Helper function to get the current trackingID for navigation
 */
export const getCurrentTrackingId = (
  accountToDisplay: ManagedAccount | undefined
): string => {
  return accountToDisplay?.trackingID || "";
};
